import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'

interface ModerationAction {
  id: string
  target_type: 'thread' | 'comment'
  target_id: string
  moderator_id: string | null
  action: 'hide' | 'warn' | 'ban' | 'restore'
  note: string | null
  created_at: string
  moderator: {
    display_name: string
  } | null
}

export default function AuditLog() {
  const [actionFilter, setActionFilter] = useState<
    'all' | 'hide' | 'warn' | 'ban' | 'restore'
  >('all')
  const [moderatorFilter, setModeratorFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')

  // Fetch all moderators for filter
  const { data: moderators } = useQuery({
    queryKey: ['moderators'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name')
        .or('role.eq.moderator,role.eq.admin,user_type.eq.moderator,user_type.eq.admin')
        .order('display_name', { ascending: true })

      if (error) throw error
      return data || []
    },
  })

  // Fetch moderation actions
  const { data: actions, isLoading } = useQuery({
    queryKey: ['moderation-actions', actionFilter, moderatorFilter, dateFrom, dateTo],
    queryFn: async () => {
      let query = supabase
        .from('moderation_actions')
        .select(`
          id,
          target_type,
          target_id,
          moderator_id,
          action,
          note,
          created_at,
          profiles!moderation_actions_moderator_id_fkey (
            display_name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(100)

      if (actionFilter !== 'all') {
        query = query.eq('action', actionFilter)
      }

      if (moderatorFilter !== 'all') {
        query = query.eq('moderator_id', moderatorFilter)
      }

      if (dateFrom) {
        query = query.gte('created_at', dateFrom)
      }

      if (dateTo) {
        query = query.lte('created_at', dateTo)
      }

      const { data, error } = await query

      if (error) throw error

      // Transform data: Supabase returns profiles as array, extract first element
      return (data || []).map((action: any) => ({
        ...action,
        moderator: Array.isArray(action.profiles)
          ? (action.profiles[0] || null)
          : action.profiles,
      })) as ModerationAction[]
    },
  })

  return (
    <div className="space-y-6">
      <h2
        className="text-2xl font-bold"
        style={{ color: 'var(--text-primary)' }}
      >
        Audit Log
      </h2>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div>
          <label
            htmlFor="action-filter"
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Action Type
          </label>
          <select
            id="action-filter"
            value={actionFilter}
            onChange={(e) =>
              setActionFilter(
                e.target.value as 'all' | 'hide' | 'warn' | 'ban' | 'restore'
              )
            }
            className="px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="all">All Actions</option>
            <option value="hide">Hide</option>
            <option value="warn">Warn</option>
            <option value="ban">Ban</option>
            <option value="restore">Restore</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="moderator-filter"
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Moderator
          </label>
          <select
            id="moderator-filter"
            value={moderatorFilter}
            onChange={(e) => setModeratorFilter(e.target.value)}
            className="px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="all">All Moderators</option>
            {moderators?.map((mod) => (
              <option key={mod.id} value={mod.id}>
                {mod.display_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="date-from"
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Date From
          </label>
          <input
            id="date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        <div>
          <label
            htmlFor="date-to"
            className="block mb-2 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Date To
          </label>
          <input
            id="date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
      </div>

      {/* Actions List */}
      {isLoading ? (
        <p style={{ color: 'var(--text-primary)' }}>Loading audit log...</p>
      ) : !actions || actions.length === 0 ? (
        <p style={{ color: 'var(--text-primary)' }}>
          No moderation actions found.
        </p>
      ) : (
        <div className="space-y-3">
          {actions.map((action) => {
            const actionDate = format(
              new Date(action.created_at),
              'dd/MM/yyyy, HH:mm:ss'
            )

            return (
              <div
                key={action.id}
                className="p-4 rounded border-2"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderColor: 'var(--bg-tertiary)',
                }}
              >
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className="px-2 py-1 text-xs font-semibold rounded"
                        style={{
                          backgroundColor:
                            action.action === 'ban' || action.action === 'hide'
                              ? 'var(--color-error)'
                              : action.action === 'warn'
                              ? 'var(--bg-interactive)'
                              : 'var(--bg-tertiary)',
                          color: 'var(--text-inverse)',
                        }}
                      >
                        {action.action.toUpperCase()}
                      </span>
                      <span
                        className="px-2 py-1 text-xs font-semibold rounded"
                        style={{
                          backgroundColor: 'var(--bg-tertiary)',
                          color: 'var(--text-primary)',
                        }}
                      >
                        {action.target_type}
                      </span>
                    </div>
                    <p
                      className="text-sm"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      Moderator: {action.moderator?.display_name || 'Unknown'}
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {actionDate}
                    </p>
                    {action.note && (
                      <p
                        className="mt-2 text-sm italic"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        Note: {action.note}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

